import Divider from '@mui/material/Divider'
import Icon from '@mui/material/Icon'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import { equals, isEmpty } from 'ramda'
import React from 'react'

import { createConfirmation } from '@/app/contexts/confirmation.context'
import { ENABLED_RETRIES, MESSAGE_PERSONALIZATION } from '@/app/module/campaigns/definitions'
import {
  CampaignTemplateType,
  LocalAction,
  LocalOnAnswerType,
  LocalOpenEnded,
  OnAnswerType,
  OpenEnded,
  SMSQuestion,
} from '@/app/module/campaigns/types'
import { unwrapActions, wrapActions } from '@/app/module/campaigns/utils/actions/wrap'
import {
  getOpenEndedValidation,
  normalizeOpenEndedValidation,
} from '@/app/module/campaigns/utils/open-ended-validation'

import ResponseSmsConfigs from './response-sms-configs'
import { SmsConfigs } from './response-sms-configs-utils'
import ResponseSmsSpecific from './response-sms-specific'
import ResponseTextOpenEnded from './response-text-open-ended'
import { AnswerType, ResponseTextProps } from './response-text-props'
import { defaultOnAnswerText, isOpenEndedModified } from './types/multimessage/content'

type Props = ResponseTextProps & {
  item: SMSQuestion
  type: CampaignTemplateType
  useAi: boolean

  changeHandler: (item: SMSQuestion) => void
}

export default function ResponseSms(props: Props) {
  const {
    item,
    id,
    personalizationList,
    senderIds,
    whatsappSenderIds,
    orgId,
    token,
    getImageProps,
    getVoiceProps,
    files = {},
    timezone,
    timezones,
    countryCode,
    countriesTimezones,
    languages,
    defaultLanguage,
    useAi,
    changeHandler,
  } = props

  const [state, setState] = React.useState(fromProps(item))

  const itemRef = React.useRef<SMSQuestion | null>()
  const changeHandlerRef = React.useRef(changeHandler)

  React.useEffect(
    () => () => {
      if (itemRef.current) {
        changeHandlerRef.current?.(itemRef.current)
      }
    },
    [],
  )

  React.useEffect(() => {
    setState(fromProps(item))
  }, [item])

  const textPersonalization = React.useMemo(
    () => ({
      ...MESSAGE_PERSONALIZATION,
      ...personalizationList,
    }),
    [personalizationList],
  )

  const handleChange = (newItem: Partial<State>) => {
    const newState = {
      ...state,
      ...newItem,
    }

    const returnToProps = backToProps(newState)
    const updatedItem = {
      ...item,
      ...returnToProps,
    } as SMSQuestion

    changeHandler(updatedItem)
    setState(newState)
  }

  const actionProps = React.useMemo(
    () => ({
      countriesTimezones,
      countryCode,
      defaultLanguage,
      files,
      hasTranscribe: false,
      index: props.index,
      languages,
      personalizationList: textPersonalization,
      senderIds,
      timezone,
      timezones,
      transcribeEnabled: false,
      whatsappSenderIds,
      getImageProps,
      getVoiceProps,
    }),
    [
      countriesTimezones,
      countryCode,
      defaultLanguage,
      files,
      languages,
      props.index,
      senderIds,
      textPersonalization,
      timezone,
      timezones,
      whatsappSenderIds,
      getImageProps,
      getVoiceProps,
    ],
  )
  const apiProps = React.useMemo(() => ({ orgId, token }), [orgId, token])

  const handleConfigsChange = React.useCallback(
    (value: SmsConfigs) => changeHandler({ ...item, ...value }),
    [item, changeHandler],
  )
  const handleValidationChange = (openEnded: LocalOpenEnded) => {
    const wasEnabled = !!getOpenEndedValidation(state.openEnded)
    const isEnabled = !!getOpenEndedValidation(openEnded)
    handleChange({
      openEnded,
      ...(!isEnabled || !wasEnabled ? { checkInvalid: false } : {}),
      ...(!isEnabled ? { invalidActions: [] } : {}),
    })
  }

  const configs = React.useMemo(() => {
    if (state.answerType === AnswerType.OpenEnded && !getOpenEndedValidation(state.openEnded)) {
      return {
        onRetriesExhausted: item.onRetriesExhausted,
        onTimeout: item.onTimeout,
        retries: item.retries,
      }
    }

    return {
      onInvalidReply: item.onInvalidReply,
      onRetriesExhausted: item.onRetriesExhausted,
      onTimeout: item.onTimeout,
      retries: item.retries,
    }
  }, [item.onInvalidReply, item.onRetriesExhausted, item.onTimeout, item.retries, state.answerType, state.openEnded])

  return (
    <div
      className="text-question-block"
      style={{
        marginTop: '2rem',
      }}
    >
      <Divider
        style={{
          margin: '1rem -24px',
        }}
      />
      <Tabs
        value={state.answerType}
        indicatorColor="primary"
        onChange={async (_, val) => {
          if (isModified(state)) {
            const confirmed = await createConfirmation({
              title: 'Change response type',
              content:
                'Switching between response types will discard your current response config. Do you want to continue?',
            })

            if (!confirmed) {
              return
            }
          }

          handleChange({
            ...generateContent(val),
            answerType: val,
          })
        }}
      >
        <Tab
          value={AnswerType.Text}
          label="Specific Response"
          style={{ zIndex: 2 }}
          icon={<Icon>chat</Icon>}
          wrapped
          className="campaign-specific-tab"
        />

        <Tab
          value={AnswerType.OpenEnded}
          label="Open Response"
          style={{ zIndex: 2 }}
          icon={<Icon>textsms</Icon>}
          wrapped
          className="campaign-open-ended-tab"
        />
      </Tabs>
      <Divider
        style={{
          margin: '-1px -24px 1rem',
        }}
      />
      {state.answerType === AnswerType.OpenEnded && state.openEnded && (
        <ResponseTextOpenEnded
          actionProps={actionProps}
          apiProps={apiProps}
          campaignType={props.type}
          id={id}
          invalidReplyActions={state.invalidActions}
          openEnded={state.openEnded}
          partType={item.type}
          transcribe={false}
          onChange={handleChange}
          onValidationChange={handleValidationChange}
        />
      )}
      {state.answerType === AnswerType.Text && (
        <ResponseSmsSpecific
          actionProps={actionProps}
          apiProps={apiProps}
          campaignType={props.type}
          id={id}
          onAnswer={state.onAnswer}
          partType={item.type}
          useAi={useAi}
          onChange={handleChange}
        />
      )}
      <Divider
        style={{
          margin: '1rem -24px',
        }}
      />
      <div style={{ display: 'flex' }}>
        <Typography color="secondary" component="div" variant="subtitle1" gutterBottom={true}>
          <b>Important:</b> If there's no reply within 24 hours, the question will be marked as done, and the survey
          will proceed to the next question (or execute "Failure" actions you specify below). Adjust the timeout in the
          "Timeout / no reply" section below.
        </Typography>
      </div>
      <ResponseSmsConfigs
        actionProps={actionProps}
        apiProps={apiProps}
        campaignType={props.type}
        configs={configs}
        id={id}
        onChange={handleConfigsChange}
      />
    </div>
  )
}

const fromOnAnswer = (onAnswer: OnAnswerType[] | undefined, answerType: AnswerType): LocalOnAnswerType[] => {
  const defaultOnAnswer = answerType === AnswerType.Text ? defaultOnAnswerText : []

  return onAnswer && !isEmpty(onAnswer)
    ? onAnswer.map((answer) => ({
        ...answer,
        actions: unwrapActions(answer.actions || []),
      }))
    : defaultOnAnswer
}

const fromOpenEnded = (openEnded?: OpenEnded): LocalOpenEnded | undefined => {
  if (!openEnded) {
    return undefined
  }

  const categories =
    'categories' in openEnded
      ? openEnded.categories?.map(({ category, actions }) => ({
          category,
          actions: unwrapActions(actions || []),
        }))
      : undefined

  return {
    ...normalizeOpenEndedValidation(openEnded),
    actions: unwrapActions(openEnded.actions || []),
    categories,
  }
}

type State = {
  answerType: AnswerType
  checkFailure: boolean
  checkInvalid: boolean
  checkTimeout: boolean
  failureActions: LocalAction[]
  invalidActions: LocalAction[]
  onAnswer: LocalOnAnswerType[]
  openEnded: LocalOpenEnded | undefined
  retries: number
  timeoutActions: LocalAction[]
  timeoutDelay: {
    unit: string
    value: number
  }
  useAi: boolean
}

export const fromProps = (item: SMSQuestion): State => {
  const { onAnswer, openEnded, onRetriesExhausted, onInvalidReply, onTimeout, retries, useAi } = item
  const showOpenEnded = !!openEnded
  const answerType = showOpenEnded ? AnswerType.OpenEnded : AnswerType.Text

  const checkFailure = !!(onRetriesExhausted?.enabled || onRetriesExhausted?.actions?.length)
  const checkInvalid = !!(onInvalidReply?.enabled || onInvalidReply?.actions?.length)
  const checkTimeout = !!(onTimeout?.enabled || onTimeout?.actions?.length || onTimeout?.timeout?.value)
  const defaultRetries = retries === undefined ? ENABLED_RETRIES : retries

  return {
    answerType,
    checkFailure,
    checkInvalid,
    checkTimeout,
    failureActions: unwrapActions(onRetriesExhausted?.actions || []),
    invalidActions: unwrapActions(onInvalidReply?.actions || []),
    onAnswer: fromOnAnswer(onAnswer, answerType),
    openEnded: fromOpenEnded(openEnded),
    retries: checkInvalid || checkTimeout ? defaultRetries : 0,
    timeoutActions: unwrapActions(onTimeout?.actions || []),
    timeoutDelay: {
      unit: onTimeout?.timeout?.unit || 'minute',
      value: Number(onTimeout?.timeout?.value || 10),
    },
    useAi,
  }
}

const toOnAnswer = (onAnswer: LocalOnAnswerType[], openEnded: LocalOpenEnded | undefined): OnAnswerType[] =>
  openEnded ? [] : onAnswer.map((item) => ({ ...item, actions: wrapActions(item.actions) }))

const toOpenEnded = (openEnded: LocalOpenEnded | undefined): OpenEnded | undefined => {
  if (!openEnded) {
    return undefined
  }

  const categories =
    'categories' in openEnded
      ? openEnded.categories?.map(({ category, actions }) => ({
          category,
          actions: wrapActions(actions),
        }))
      : undefined

  return {
    ...normalizeOpenEndedValidation(openEnded),
    actions: wrapActions(openEnded.actions || []),
    categories,
  }
}

export const backToProps = ({
  checkFailure,
  checkInvalid,
  checkTimeout,
  failureActions,
  invalidActions,
  onAnswer,
  openEnded,
  retries = 0,
  timeoutActions,
  timeoutDelay,
  useAi,
}: State): Partial<SMSQuestion> => ({
  onAnswer: toOnAnswer(onAnswer, openEnded),
  onInvalidReply: {
    enabled: checkInvalid && (!openEnded || !!getOpenEndedValidation(openEnded)),
    actions: checkInvalid && (!openEnded || getOpenEndedValidation(openEnded)) ? wrapActions(invalidActions) : [],
  },
  onRetriesExhausted: {
    enabled: checkFailure,
    actions: checkFailure ? wrapActions(failureActions) : [],
  },
  onTimeout: {
    enabled: checkTimeout,
    timeout: checkTimeout ? timeoutDelay : undefined,
    actions: checkTimeout ? wrapActions(timeoutActions) : [],
  },
  openEnded: toOpenEnded(openEnded),
  retries,
  useAi,
})

type SMSQuestionContent = {
  onAnswer: LocalOnAnswerType[]
  openEnded: LocalOpenEnded | undefined
  useAi: boolean
}

export const generateContent = (answerType: AnswerType): SMSQuestionContent => {
  switch (answerType) {
    case AnswerType.OpenEnded:
      return {
        onAnswer: [],
        openEnded: {
          actions: [],
        },
        useAi: false,
      }

    default:
      return {
        onAnswer: defaultOnAnswerText,
        openEnded: undefined,
        useAi: false,
      }
  }
}

const isModified = ({ answerType, onAnswer, openEnded, useAi }: State) => {
  const defaultOnAnswer = defaultOnAnswerText

  const isAnswerModified = answerType === AnswerType.Text && !equals(onAnswer, defaultOnAnswer)
  const isOpenModified = answerType === AnswerType.OpenEnded && isOpenEndedModified(openEnded)

  return isAnswerModified || isOpenModified || useAi
}

export type { LocalAction, LocalOnAnswerType, State }
